import {
  createRootRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { Toaster } from "sonner";
import { AuthGuard } from "@/components/common/auth-guard/auth-guard";
import { OfflineChip } from "@/components/common/offline-chip/offline-chip";
import { AppUpdateBanner } from "@/components/common/app-update-banner/app-update-banner";
import { BuildStampChip } from "@/components/common/build-stamp-chip/build-stamp-chip";
import { QueryErrorBoundary } from "@/components/query-error-boundary";
import { RouteErrorFallback } from "@/components/common/route-error-fallback/route-error-fallback";

/** Path prefixes for the public link pages: the project share page, and an
 * issue link's page. Routes under them render OUTSIDE AuthGuard so an
 * anonymous visitor following a link is never bounced to WorkOS — the token in
 * the URL is the credential. The URLs are fixed (the backend mints
 * `/projects/share/{token}` and `/i/{token}` into live links), so we gate by
 * path here rather than relocating the routes under a `/public` prefix. Every
 * other route stays behind AuthGuard. See [[share-page.spec]] and
 * [[issue-invite-page.spec]]. */
const PUBLIC_PREFIXES = ["/projects/share/", "/i/"];

function RootLayout() {
  const pathname = useRouterState({
    select: (s) => s.location.pathname,
  });

  // Public link pages: skip AuthGuard entirely. Still wrapped in the query
  // error boundary, and AppUpdateBanner stays mounted (service-worker update
  // detection runs regardless of auth state).
  if (PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return (
      <>
        <QueryErrorBoundary>
          <Outlet />
        </QueryErrorBoundary>
        <AppUpdateBanner />
        <BuildStampChip />
      </>
    );
  }

  return (
    <>
      <AuthGuard>
        <QueryErrorBoundary>
          <Outlet />
        </QueryErrorBoundary>
        <OfflineChip />
        <Toaster
          position="bottom-center"
          offset="calc(env(safe-area-inset-bottom) + 8px)"
          visibleToasts={3}
          duration={5000}
          toastOptions={{
            classNames: {
              toast:
                "!bg-foreground !text-background !border-transparent !rounded-2xl !shadow-lg !font-sans !px-3 !py-2",
              title: "!text-background !text-xs !font-medium",
            },
          }}
        />
      </AuthGuard>
      {/* Outside AuthGuard so the service worker registers (and update
          detection runs) regardless of auth state — matching the prior
          module-scope registration in main.tsx. */}
      <AppUpdateBanner />
      {/* Also outside AuthGuard: which build you are on is worth knowing on the
          sign-in screen too. Renders nothing in production. */}
      <BuildStampChip />
    </>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RouteErrorFallback,
});
